import { categoryLabels } from "@/lib/categoryLabels";
import type { ItemCategory } from "@/lib/types";

const BASE_PROHIBITED_ITEMS: string[] = [
  "Counterfeit or replica items (counterfeit money, fake designer items, etc.)",
  "Extremely large or bulky items that may be difficult to transport",
  "Fashion items (clothing, shoes, bags, hair products, makeup/accessories, jewelry, etc.)",
  "Faulty electronics or items not in good working condition",
  "Hazardous materials (explosives, chemicals, flammable substances, etc.)",
  "Illegal items or substances (drugs, weapons, etc.)",
  "Items that infringe on intellectual property rights (e.g., pirated software, counterfeit goods)",
  "Items that are not in the spirit of the platform (e.g., adult content, hate speech materials)",
  "Live animals or animal products from endangered species",
  "Medical equipment, medications, or health supplements",
  "Perishable items (food, flowers, plants, etc.)",
  "Services or digital products with no physical component",
  "Vehicles (cars, motorcycles, tricycles/keke, etc.)",
];

export interface TermsItemPolicies {
  acceptedItems: string[];
  prohibitedItems: string[];
  deactivatedCategoryLabels: string[];
}

function toKnownCategoryIds(ids: string[]): ItemCategory[] {
  const known = new Set(Object.keys(categoryLabels));
  return ids.filter((id): id is ItemCategory => known.has(id));
}

export function buildTermsItemPolicies(
  activeCategoryIds?: string[],
): TermsItemPolicies {
  const allCategoryIds = Object.keys(categoryLabels) as ItemCategory[];
  const activeIds = new Set<ItemCategory>(
    activeCategoryIds && activeCategoryIds.length > 0
      ? toKnownCategoryIds(activeCategoryIds)
      : allCategoryIds,
  );

  const acceptedItems = allCategoryIds
    .filter((id) => activeIds.has(id))
    .map((id) => categoryLabels[id])
    .sort((a, b) => a.localeCompare(b));

  const deactivatedCategoryLabels = allCategoryIds
    .filter((id) => !activeIds.has(id))
    .map((id) => categoryLabels[id])
    .sort((a, b) => a.localeCompare(b));

  const prohibitedItems =
    deactivatedCategoryLabels.length > 0
      ? [
          `Currently deactivated categories on Sell It Off: ${deactivatedCategoryLabels.join(", ")}.`,
          ...BASE_PROHIBITED_ITEMS,
        ]
      : BASE_PROHIBITED_ITEMS;

  return {
    acceptedItems,
    prohibitedItems,
    deactivatedCategoryLabels,
  };
}
